import { GROUP_COLOURS, useStore } from "../store.js";

/** Actions for a multi-node selection: grouping and bulk delete. */
export function BulkPanel() {
  const selectedNodeIds = useStore((state) => state.selectedNodeIds);
  const groups = useStore((state) => state.groups);
  const deleteNodes = useStore((state) => state.deleteNodes);
  const setSelection = useStore((state) => state.setSelection);
  const createGroup = useStore((state) => state.createGroup);
  const renameGroup = useStore((state) => state.renameGroup);
  const recolourGroup = useStore((state) => state.recolourGroup);
  const toggleCollapse = useStore((state) => state.toggleCollapse);
  const ungroup = useStore((state) => state.ungroup);

  if (selectedNodeIds.length < 2) {
    return null;
  }

  // If every selected node belongs to the same single group, treat this as that
  // group being selected (so we offer rename/ungroup instead of re-grouping).
  const groupOf = (nodeId: string) => groups.find((group) => group.nodeIds.includes(nodeId));
  const groupIds = new Set(selectedNodeIds.map((id) => groupOf(id)?.id));
  const sharedGroup =
    groupIds.size === 1 && !groupIds.has(undefined)
      ? groups.find((group) => group.id === [...groupIds][0])
      : undefined;

  return (
    <aside className="pointer-events-auto absolute left-4 top-4 flex w-72 flex-col gap-3 rounded-2xl border border-node-border bg-node-bg/95 p-5 shadow-2xl backdrop-blur">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-ink-muted">
          {sharedGroup ? `Group: ${sharedGroup.name}` : `${selectedNodeIds.length} nodes selected`}
        </span>
        <button
          type="button"
          onClick={() => setSelection([], [])}
          className="text-sm text-ink-muted hover:text-ink"
        >
          Clear
        </button>
      </div>

      {sharedGroup ? (
        <>
          <button
            type="button"
            onClick={() => {
              const name = window.prompt("Rename group", sharedGroup.name)?.trim();
              if (name) {
                void renameGroup(sharedGroup.id, name);
              }
            }}
            className="rounded-lg border border-node-border px-3 py-1.5 text-sm text-ink hover:border-accent"
          >
            Rename group
          </button>
          <div>
            <span className="mb-1 block text-xs text-ink-muted">Colour</span>
            <div className="flex gap-2">
              {GROUP_COLOURS.map((colour) => (
                <button
                  key={colour}
                  type="button"
                  aria-label="Set group colour"
                  onClick={() => void recolourGroup(sharedGroup.id, colour)}
                  className={`h-6 w-6 rounded-full border-2 ${
                    sharedGroup.color === colour ? "border-ink" : "border-node-border"
                  }`}
                  style={{ backgroundColor: colour }}
                />
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={() => void toggleCollapse(sharedGroup.id)}
            className="rounded-lg border border-node-border px-3 py-1.5 text-sm text-ink hover:border-accent"
          >
            {sharedGroup.collapsed ? "Expand" : "Collapse"}
          </button>
          <button
            type="button"
            onClick={() => void ungroup(sharedGroup.id)}
            className="rounded-lg border border-node-border px-3 py-1.5 text-sm text-ink hover:border-accent"
          >
            Ungroup
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={() => {
            const name = window.prompt("Name this group")?.trim();
            if (name) {
              void createGroup(name, [...selectedNodeIds]);
            }
          }}
          className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-canvas hover:opacity-90"
        >
          Group these {selectedNodeIds.length} nodes
        </button>
      )}

      <button
        type="button"
        onClick={() => void deleteNodes([...selectedNodeIds])}
        className="rounded-lg border border-warning/50 px-3 py-1.5 text-sm text-warning hover:bg-warning/10"
      >
        Delete {selectedNodeIds.length} nodes
      </button>
    </aside>
  );
}
